import { useQuery } from '@apollo/client';
import type {
  UseSegmentsListFetch,
  UseSegmentsListFetchProps,
} from '@openx/components-segment/lib/utils/context/SegmentHookTypes.ts';
import type { FetchMore } from '@openx/components-segment/types.ts';
import type { QueryVar } from '@openx/types/serverSideCriteria.ts';
import { useCallback } from 'react';
import {
  GetSegmentFeedDocument,
  type GetSegmentFeedQuery,
  type GetSegmentFeedQueryVariables,
} from '../../../graphql/query/segments/GetSegmentFeed.generated.tsx';
import { GetSegmentsListDocument } from '../../../graphql/query/segments/GetSegmentsList.generated.tsx';

type QueryParams = GetSegmentFeedQueryVariables & QueryVar;

export const useSegmentsListFetch: UseSegmentsListFetch = ({
  variables,
  options,
  usePrefetch,
  skip = false,
}: UseSegmentsListFetchProps<QueryParams>) => {
  const { client, data, error, loading } = useQuery(GetSegmentsListDocument, {
    onCompleted: ({ segment_aggregate }) => options?.onCompleted?.(segment_aggregate.aggregate?.count),
    variables,
    skip,
  });

  const { segmentFeed = [] } = data ?? {};

  // Ensure provider is always a defined Pick<Provider, "id" | "name">
  // segments should be required to have a provider on the api
  const segments = segmentFeed.map(segment => ({
    ...segment,
    provider: segment.provider ? { id: segment.provider.id, name: segment.provider.name } : { id: '', name: '' },
  }));

  const fetchMore: FetchMore<QueryParams> = useCallback(
    queryOptions => {
      client.query<GetSegmentFeedQuery, GetSegmentFeedQueryVariables>({
        query: GetSegmentFeedDocument,
        variables: { ...variables, ...queryOptions.variables },
      });
    },
    [variables, client]
  );

  usePrefetch?.(fetchMore);

  return {
    error,
    loading,
    segments,
    totalCount: data?.segment_aggregate?.aggregate?.count ?? 0,
  };
};
